import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

export interface CopyButtonProps {
  /** Text written to the clipboard. Hidden when empty. */
  value: string | null | undefined;
  /** Accessible name. Defaults to common `copyField` with the value. */
  label?: string;
}

/**
 * Accessible copy-to-clipboard icon button with success/failure toasts (#2671).
 */
export function CopyButton({ value, label }: CopyButtonProps) {
  const { t: tc } = useTranslation('common');
  const { copy, copied } = useCopyToClipboard();
  const text = value?.trim() ?? '';

  if (text.length === 0) {
    return null;
  }

  const accessibleName = label ?? tc('copyField', { field: text });
  const tooltip = copied ? tc('copied') : accessibleName;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    void copy(text);
  };

  return (
    <Tooltip title={tooltip}>
      <IconButton
        type="button"
        size="small"
        onClick={handleClick}
        aria-label={accessibleName}
      >
        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}
